import { Text, IconSize, Icon, Spinner, Box } from '@bechtel/gds';
import { EventNotification, EventNotificationSortByTerm, EventNotificationStatus } from '@bechtel/types';
import { t } from '@lingui/macro';
import { get, join } from 'lodash';
import { useDate, TextWithEllipsis, EllipsisPlacement } from '@bechtel/client-common';
import { Fragment } from 'react';

export function useTableHeaders() {
  const { formatISODate, formatTime } = useDate();
  const tableHeaders = [
    {
      id: 'Entity Type',
      label: t`Entity Type`,
      key: 'entityTypeId',
      sortKey: EventNotificationSortByTerm['ENTITY_TYPE_ID'],
    },
    {
      id: 'Identifier',
      label: t`Identifier`,
      key: 'identifier.0',
      render({ identifier }: EventNotification) {
        return <Text size="300">{join(identifier, ', ')}</Text>;
      },
    },
    {
      id: 'Status',
      label: t`Status`,
      key: 'status',
      sortKey: EventNotificationSortByTerm['STATUS'],
      render({ status }: { status: EventNotificationStatus }) {
        return (
          <Text size="300" color={status === EventNotificationStatus.ERROR ? 'red.600' : 'green.600'}>
            {status}
          </Text>
        );
      },
    },
    { id: 'Error Type', label: t`Error Type`, key: 'errorTypeId' },
    { id: 'Notification Type', label: t`Notification Type`, key: 'notificationTypeId' },
    { id: 'Error Description', label: t`Error Description`, key: 'errorDescription' },
    {
      id: 'Error Summary',
      label: t`Error Summary`,
      key: 'error.error',
      render({ error }: EventNotification) {
        const errorDetail = JSON.stringify(error);
        return (
          <TextWithEllipsis size="300" title={errorDetail} ellipsisPlacement={EllipsisPlacement.END}>
            {errorDetail}
          </TextWithEllipsis>
        );
      },
    },
    {
      id: 'Created At',
      label: t`Created At`,
      key: 'createdAt',
      sortKey: EventNotificationSortByTerm['CREATED_AT'],
      render({ createdAt }: EventNotification) {
        return (
          <Box flexDirection={{ sm: 'column' }}>
            <Box mb={{ sm: '200' }}>
              <Text size="300">{formatISODate(createdAt)}</Text>
            </Box>
            <Text size="300">{formatTime(createdAt)}</Text>
          </Box>
        );
      },
    },
    {
      id: 'Retry Count',
      label: t`Retry Count`,
      key: 'retryCount',
      sortKey: EventNotificationSortByTerm['RETRY_COUNT'],
    },
    {
      id: 'Next Scheduled At',
      label: t`Next Scheduled At`,
      key: 'nextScheduleAt',
      sortKey: EventNotificationSortByTerm['NEXT_SCHEDULE_AT'],
      render({ nextScheduleAt }: EventNotification) {
        return (
          <Fragment>
            {nextScheduleAt ? (
              <Box flexDirection={{ sm: 'column' }}>
                <Box mb={{ sm: '200' }}>
                  <Text size="300">{formatISODate(nextScheduleAt)}</Text>
                </Box>
                <Text size="300">{formatTime(nextScheduleAt)}</Text>
              </Box>
            ) : (
              <></>
            )}
          </Fragment>
        );
      },
    },
    {
      id: 'Action',
      label: t`Action`,
      key: 'action',
      render({ loading, onSync }) {
        return loading ? (
          <Spinner />
        ) : (
          <Icon size={IconSize.MEDIUM} icon="rotate" fillColor="bechtel.blue.800" onClick={() => onSync()} />
        );
      },
    },
  ];
  return tableHeaders;
}
